/**
 * Mobile device detection utilities
 * Provides helpers for detecting mobile devices and device capabilities
 */

/**
 * Detects if the current device is a mobile device based on user agent
 * @returns true if device is mobile (phone or tablet)
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false // SSR safe
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera

  // Check for mobile patterns in user agent
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
  return mobileRegex.test(userAgent)
}

/**
 * Detects if the current device is an iOS device
 * @returns true if device is iPhone, iPad, or iPod
 */
export function isIOS(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera

  // Check for iOS patterns
  return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream
}

/**
 * Detects if the current device is an Android device
 * @returns true if device is Android
 */
export function isAndroid(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera
  return /Android/i.test(userAgent)
}

/**
 * Gets the device type based on screen size and user agent
 * @returns 'mobile' | 'tablet' | 'desktop'
 */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') {
    return 'desktop' // SSR default
  }

  const width = window.innerWidth
  const userAgent = navigator.userAgent || ''

  // Tablet detection
  if (width >= 768 && width < 1024) {
    return 'tablet'
  }

  // iPad specific detection (iOS 13+ reports as desktop)
  if (/iPad/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'tablet'
  }

  // Mobile detection
  if (width < 768 || isMobileDevice()) {
    return 'mobile'
  }

  return 'desktop'
}

/**
 * Checks if the browser supports the HTML5 capture attribute for file inputs
 * @returns true if capture attribute is supported
 */
export function supportsCapture(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false
  }

  const input = document.createElement('input')
  input.type = 'file'

  // Check if capture attribute is supported
  return 'capture' in input
}

/**
 * Gets the recommended touch target size based on device type
 * @returns minimum touch target size in pixels
 */
export function getTouchTargetSize(): number {
  const deviceType = getDeviceType()

  // iOS Human Interface Guidelines recommend 44px minimum
  // Android Material Design recommends 48dp (≈48px)
  return deviceType === 'mobile' ? 44 : 32
}

/**
 * Checks if the device is in portrait orientation
 * @returns true if device is in portrait mode
 */
export function isPortrait(): boolean {
  if (typeof window === 'undefined') {
    return true
  }

  return window.innerHeight > window.innerWidth
}

/**
 * Checks if the device is in landscape orientation
 * @returns true if device is in landscape mode
 */
export function isLandscape(): boolean {
  return !isPortrait()
}

/**
 * Gets the current viewport dimensions
 * @returns object with width and height in pixels
 */
export function getViewportDimensions(): { width: number; height: number } {
  if (typeof window === 'undefined') {
    return { width: 1024, height: 768 } // SSR default
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight
  }
}

/**
 * Checks if the device supports touch events
 * @returns true if touch is supported
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  )
}
